import React, { useState } from "react";
import { asText, CardShell, EmptyRow, formatCompact, formatCurrency, formatRoas, LoadingRow, unwrapValue, useCxoResource } from "../shared";
import { NO_PERFORMANCE_HEX, nigeriaOutlinePath, performanceLegend, projectLatLng, regionPerformance, shortRegionName } from "../../nigeria-map";

// Generic Regions tab — identical shape at every level: "highlights (top
// region, fastest growing with from/to, lowest cost per result,
// underperforming = lowest ROAS), the map/table rows (lat, lng, raw roas for
// the marker colour), top_by_results for the Top 5 bars, and per-region
// metrics with change." Confirmed live: each item's real name field is
// `region` (not `name`), and `underperforming`'s own change field is
// `results_change_pct` (not `change_pct`) — both read defensively with a
// `name` fallback in case a future response shape changes again. Map is a
// lightweight custom visual (no mapping dependency/GeoJSON, per earlier
// direct ask) — markers placed from real lat/lng against Nigeria's real
// bounding box, not a geographically accurate coastline. Marker/row color
// and the legend come straight from each region's own backend `performance`
// rating + hex (see regionPerformance in nigeria-map.ts), same as the
// Overview page's Birds Eye map (cxo-dashboard/index.tsx).
//
// Markers always show their name + results inline (per direct ask) — a real
// tenant can have 30+ regions with real lat/lng close together (Nigeria's 36
// states + FCT), so labels can overlap at that scale; click a marker for its
// full detail card regardless, same popup pattern as the Overview page's own
// Birds Eye map.

// Shortened everywhere this is used (map labels, popup, highlight cards) —
// per direct ask, to leave more room on the map before labels overlap.
const regionLabel = (r: any) => shortRegionName(asText(r.region ?? r.name, "N/A"));

// Real Nigeria border outline + projector, shared with the Overview page's
// own map (cxo-dashboard/index.tsx) — see nigeria-map.ts.
// Scaled up (same 300:320 ratio) alongside the taller container below, to
// give the now-always-on labels (showInlineLabels) more room.
const MAP_W = 420;
const MAP_H = 448;
// Map + click-for-detail popup + tier legend, shared by the full Regions tab
// and the ad Performance tab's condensed view (ad-detail.tsx) — `height` is
// the only thing that differs between the two.
export const RegionMap: React.FC<{ items: any[]; height?: number }> = ({ items, height = 420 }) => {
  const [selected, setSelected] = useState<any | null>(null);
  const placeable = items.filter((r) => unwrapValue(r.lat) != null && unwrapValue(r.lng) != null);
  // Per direct ask: every marker always shows its name + results inline now,
  // regardless of count (previously gated off above 15 to avoid overlap at
  // the unscoped/tenant-wide view — that trade-off is overridden here).
  const showInlineLabels = true;
  const { legend, basis } = performanceLegend(items.map(regionPerformance));
  const selectedPerf = selected ? regionPerformance(selected) : null;

  return (
    <>
      {placeable.length === 0 ? (
        <EmptyRow label="None of these regions have a placeable location yet." />
      ) : (
        <div className="relative rounded-xl overflow-hidden bg-sky-50 dark:bg-gray-950" style={{ height }}>
          <svg viewBox={`0 0 ${MAP_W} ${MAP_H}`} className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
            <path d={nigeriaOutlinePath(MAP_W, MAP_H)} className="fill-emerald-100 dark:fill-emerald-900/40 stroke-emerald-400 dark:stroke-emerald-600" strokeWidth={1.5} />
            {placeable.map((r, i) => {
              const { x, y } = projectLatLng(unwrapValue(r.lat), unwrapValue(r.lng), MAP_W, MAP_H, 14);
              const color = regionPerformance(r)?.hex ?? NO_PERFORMANCE_HEX;
              const isSelected = selected && regionLabel(selected) === regionLabel(r);
              const results = unwrapValue(r.results ?? r.metrics?.results);
              return (
                <g key={i} onClick={() => setSelected(r)} className="cursor-pointer">
                  <circle cx={x} cy={y} r={isSelected ? 10 : 7} fill={color} fillOpacity={0.25} />
                  <circle cx={x} cy={y} r={isSelected ? 5.5 : 4} fill={color} stroke="white" strokeWidth={1.5} />
                  {/* Few enough regions at this scope (a single campaign's
                      own regions, not the tenant-wide 38) to label every
                      marker inline without the text overlapping — per
                      direct ask, no click needed to see name + results. */}
                  {showInlineLabels && (
                    <>
                      <text x={x + 8} y={y - 4} fontSize={9} fontWeight={600} className="fill-gray-700 dark:fill-gray-200">
                        {regionLabel(r)}
                      </text>
                      <text x={x + 8} y={y + 7} fontSize={8} className="fill-gray-500 dark:fill-gray-400">
                        {results != null ? `${formatCompact(results)} results` : ""}
                      </text>
                    </>
                  )}
                </g>
              );
            })}
          </svg>

          {selected && (
            <div className="absolute top-2 left-2 w-52 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-100 dark:border-gray-800 p-3 text-xs">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{regionLabel(selected)}</p>
                <button type="button" onClick={() => setSelected(null)} className="text-gray-300 hover:text-gray-500 dark:hover:text-gray-300 leading-none">
                  ×
                </button>
              </div>
              <div className="space-y-1 text-gray-600 dark:text-gray-300">
                <div className="flex justify-between">
                  <span>Spend</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(unwrapValue(selected.spend ?? selected.metrics?.spend), "₦0.00")}</span>
                </div>
                <div className="flex justify-between">
                  <span>Reach</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{formatCompact(unwrapValue(selected.reach ?? selected.metrics?.reach), "0")}</span>
                </div>
                <div className="flex justify-between">
                  <span>Results</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{formatCompact(unwrapValue(selected.results ?? selected.metrics?.results), "0")}</span>
                </div>
                <div className="flex justify-between">
                  <span>Cost per result</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(unwrapValue(selected.cost_per_result ?? selected.metrics?.cost_per_result), "₦0.00")}</span>
                </div>
                <div className="flex justify-between">
                  <span>ROAS</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{formatRoas(unwrapValue(selected.roas ?? selected.metrics?.roas), "N/A")}</span>
                </div>
                <div className="flex justify-between items-center pt-1">
                  <span>Performance</span>
                  <span className="inline-flex items-center gap-1 font-medium text-gray-900 dark:text-gray-100">
                    <span className="w-2 h-2 rounded-full" style={{ background: selectedPerf?.hex ?? NO_PERFORMANCE_HEX }} />
                    {selectedPerf ? selectedPerf.label : "N/A"}
                    {selectedPerf?.vsAveragePct != null && <span className="font-normal text-gray-400 dark:text-gray-500">({selectedPerf.vsAveragePct > 0 ? "+" : ""}{selectedPerf.vsAveragePct.toFixed(1)}% vs avg)</span>}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {legend.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[11px] text-gray-500 dark:text-gray-400">
          {legend.map((p) => (
            <span key={p.rating} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: p.hex }} /> {p.label}
            </span>
          ))}
          {basis && <span className="text-gray-400 dark:text-gray-500">Rated on {basis} vs the regional average</span>}
        </div>
      )}
    </>
  );
};

const RegionsTab: React.FC<{ basePath: string; entityId: string; baseParams: Record<string, any> }> = ({ basePath, entityId, baseParams }) => {
  const { data, loading } = useCxoResource<any>(basePath, `${entityId}/regions`, baseParams, !!entityId);
  if (loading && !data) return <LoadingRow label="Loading regional performance…" />;
  const items: any[] = Array.isArray(data?.items) ? data.items : [];
  if (!data || items.length === 0) return <EmptyRow label="No regional performance data for this period yet." />;

  const highlights = data.highlights || {};
  // top_by_results can come back a real but empty array (confirmed live —
  // e.g. every region at 0 results for the period) rather than missing
  // entirely; falling back to items sorted by results still gives a useful
  // ranking instead of an empty card in that case.
  const topByResults: any[] = Array.isArray(data.top_by_results) && data.top_by_results.length > 0 ? data.top_by_results : [...items].sort((a, b) => (Number(unwrapValue(b.results ?? b.metrics?.results)) || 0) - (Number(unwrapValue(a.results ?? a.metrics?.results)) || 0));
  const maxResults = Math.max(...topByResults.map((r) => Number(unwrapValue(r.results ?? r.metrics?.results)) || 0), 1);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Top region</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{regionLabel(highlights.top_region || {})}</p>
          {highlights.top_region?.results != null && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatCompact(highlights.top_region.results)} results</p>}
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Fastest growing region</p>
          <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">{regionLabel(highlights.fastest_growing || {})}</p>
          {(highlights.fastest_growing?.change_pct ?? highlights.fastest_growing?.results_change_pct) != null && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              ↑ {Number(unwrapValue(highlights.fastest_growing.change_pct ?? highlights.fastest_growing.results_change_pct)).toFixed(0)}% results
              {highlights.fastest_growing.from != null && highlights.fastest_growing.to != null ? ` from ${highlights.fastest_growing.from} to ${highlights.fastest_growing.to}` : ""}
            </p>
          )}
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Lowest cost per result</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{regionLabel(highlights.lowest_cost_per_result || {})}</p>
          {highlights.lowest_cost_per_result?.cost_per_result != null && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatCurrency(highlights.lowest_cost_per_result.cost_per_result)}</p>}
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Underperforming region</p>
          <p className="text-sm font-semibold text-rose-500">{regionLabel(highlights.underperforming || {})}</p>
          {highlights.underperforming?.roas != null && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatRoas(highlights.underperforming.roas)} ROAS</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CardShell title="Regional performance map" description="Results distribution across regions — click a marker for more detail.">
          <RegionMap items={items} />
        </CardShell>

        <CardShell title="Regional performance" description="Compare key metrics across regions.">
          <div className="overflow-x-auto -mx-1 max-h-[340px] overflow-y-auto">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 tracking-wide">
                  <th className="pb-2 pl-1">REGION</th>
                  <th className="pb-2">SPEND</th>
                  <th className="pb-2">REACH</th>
                  <th className="pb-2">RESULTS</th>
                  <th className="pb-2">COST/RESULT</th>
                  <th className="pb-2 pr-1 text-right">ROAS</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => {
                  const roas = unwrapValue(r.roas ?? r.metrics?.roas);
                  return (
                    <tr key={i} className="border-t border-gray-50 dark:border-gray-800">
                      <td className="py-2 pl-1">
                        <span className="flex items-center gap-1.5 font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: regionPerformance(r)?.hex ?? NO_PERFORMANCE_HEX }} />
                          {regionLabel(r)}
                        </span>
                      </td>
                      <td className="py-2 text-gray-600 dark:text-gray-300">{formatCurrency(unwrapValue(r.spend ?? r.metrics?.spend), "₦0.00")}</td>
                      <td className="py-2 text-gray-600 dark:text-gray-300">{formatCompact(unwrapValue(r.reach ?? r.metrics?.reach), "0")}</td>
                      <td className="py-2 text-gray-600 dark:text-gray-300">{formatCompact(unwrapValue(r.results ?? r.metrics?.results), "0")}</td>
                      <td className="py-2 text-gray-600 dark:text-gray-300">{formatCurrency(unwrapValue(r.cost_per_result ?? r.metrics?.cost_per_result), "₦0.00")}</td>
                      <td className="py-2 pr-1 text-right font-semibold text-emerald-600 dark:text-emerald-400">{formatRoas(roas, "N/A")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardShell>
      </div>

      <CardShell title="Top 5 regions by results" description="Comparison of results across top performing regions.">
        <div className="space-y-3">
          {topByResults.slice(0, 5).map((r, i) => {
            const results = Number(unwrapValue(r.results ?? r.metrics?.results)) || 0;
            return (
              <div key={i} className="flex items-center gap-3">
                <span className="w-24 shrink-0 text-sm text-gray-600 dark:text-gray-300 truncate">{regionLabel(r)}</span>
                <div className="flex-1 h-3 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                  <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.max(4, (results / maxResults) * 100)}%` }} />
                </div>
                <span className="w-16 shrink-0 text-sm text-right font-medium text-gray-900 dark:text-gray-100">{formatCompact(results)}</span>
              </div>
            );
          })}
        </div>
      </CardShell>
    </div>
  );
};

export default RegionsTab;
